// src/App.js

import React, { useEffect, useRef } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { getSocket } from './socket';
import ProtectedRoute from './ProtectedRoute';
import Login from './Login';
import SolumKnowledgeBase from './SolumKnowledgeBase';
import SolumToolSuite from './SolumToolSuite';
import SolumToolSuiteLayout from './SolumToolSuiteLayout';
import SolumSaasSuite from './SolumSaasSuite';
import MainHomepage from './MainHomepage';
import Register from './Register';
import GatewayTester from './GatewayTester';
import BadgeManagement from './BadgeManagement';
import FunctionAssignment from './FunctionAssignment';
import LogFileReader from './LogFileReader';
import LicenseManagement from './LicenseManagement';
import RedmineProjectsExport from './RedmineProjectsExport';
import DragAndDropUploader from './DragAndDropUploader';
import WalmartRoutersDashboard from './WalmartRoutersDashboard';
import GatewaySSHManager from './GatewaySshManager';
import LowesGatewayFwRevert from './LowesGatewayFwRevert';
import Videos from './Videos';
import RFIDBatchAssign from './RFIDBatchAssign';
import AuditLogs from './AuditLogs';
import OfflineLabelsAdmin from './OfflineLabelsAdmin';
import OfflineGatewayAnalysis from './OfflineGatewayAnalysis';
import VideoSync from './VideoSync';
import GlobalActionCenter from './GlobalActionCenter';
import DigitalSignageMaintenance from './DigitalSignageMaintenance';
import GatewayApiDocs from './GatewayApiDocs';
import WebhookMonitor from './WebhookMonitor';
import WebhookCallButton from './WebhookCallButton';
import SafeOutToggle from './SafeOutToggle';
import GatewayFirmwareManager from './GatewayFirmwareManager';
import SalesRolloutForecasting from './SalesRolloutForecasting';
import VlanManager from './VlanManager';
import BulkUserUpload from './BulkUserUpload';
import RedminePortal from './components/redmine/RedminePortal';
import NetworkScanner from './NetworkScanner';
import PublicUpload from './PublicUpload';
import UserProfile from './UserProfile';
import TimeOffApp from './hr/timeoff/TimeOffApp';
import HrPortalApp from './hr/portal/HrPortalApp';

function App() {
    const location = useLocation();
    const previousPage = useRef(null);

    // Track page navigation via WebSocket
    useEffect(() => {
        const socket = getSocket();
        if (!socket) return;

        const emitPage = () => {
            socket.emit('page:navigate', {
                page: location.pathname,
                previousPage: previousPage.current
            });
            previousPage.current = location.pathname;
        };

        if (socket.connected) {
            emitPage();
        } else {
            socket.once('connect', emitPage);
        }

        return () => socket.off('connect', emitPage);
    }, [location.pathname]);

    // Define routes where the layout should not be rendered
    const excludedRoutes = ['/login', '/register', '/solum-tool-suite', '/solum-saas-suite'];

    // Determine if the current route is excluded
    const isExcludedRoute = excludedRoutes.includes(location.pathname)
        || location.pathname.startsWith('/public-upload');

    return (
        <>
            {/* Conditionally render the layout based on the current route */}
            {!isExcludedRoute && <SolumToolSuiteLayout isDropdown />}
            <Routes>
                {/* Public Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/videos" element={<Videos />} />
                <Route path="/public-upload/:token" element={<PublicUpload />} />

                {/* Super-Admin-Only Routes */}
                <Route element={<ProtectedRoute requiredRole="superadmin" />}>
                    <Route path="/audit-logs" element={<AuditLogs />} />
                </Route>

                {/* Admin-Only Routes (admin tools) */}
                <Route element={<ProtectedRoute requiredRole="admin" />}>
                    <Route path="/gateway-api-docs" element={<GatewayApiDocs />} />
                    <Route path="/redmine-portal" element={<RedminePortal />} />
                    <Route path="/safe-out" element={<SafeOutToggle />} />
                </Route>

                {/* All authenticated users — ProtectedComponent handles per-function access */}
                <Route element={<ProtectedRoute />}>
                    <Route path="/" element={<MainHomepage />} />
                    <Route path="/main-homepage" element={<MainHomepage />} />
                    <Route path="/user-profile" element={<UserProfile />} />
                    <Route path="/solum-knowledge-base" element={<SolumKnowledgeBase />} />
                    <Route path="/solum-tool-suite/*" element={<SolumToolSuite />} />
                    <Route path="/solum-saas-suite/*" element={<SolumSaasSuite />} />
                    <Route path="/gateway-tester" element={<GatewayTester />} />
                    <Route path="/function-assignment" element={<FunctionAssignment />} />
                    <Route path="/log-file-reader" element={<LogFileReader />} />
                    <Route path="/license-management" element={<LicenseManagement />} />
                    <Route path="/redmine-projects-export" element={<RedmineProjectsExport />} />
                    <Route path="/drag-and-drop-uploader" element={<DragAndDropUploader />} />
                    <Route path="/router-fleet-status" element={<WalmartRoutersDashboard />} />
                    <Route path="/gateway-ssh-manager" element={<GatewaySSHManager />} />
                    <Route path="/lowes-gateway-fw-revert" element={<LowesGatewayFwRevert />} />
                    <Route path="/badge-management" element={<BadgeManagement />} />
                    <Route path="/rfid-batch-assign" element={<RFIDBatchAssign />} />
                    <Route path="/offline-labels" element={<OfflineLabelsAdmin />} />
                    <Route path="/offline-gateways" element={<OfflineGatewayAnalysis />} />
                    <Route path="/video-sync" element={<VideoSync />} />
                    <Route path="/global-action-center" element={<GlobalActionCenter />} />
                    <Route path="/digital-signage-maintenance" element={<DigitalSignageMaintenance />} />
                    <Route path="/webhook-monitor" element={<WebhookMonitor />} />
                    <Route path="/webhook-call-button" element={<WebhookCallButton />} />
                    <Route path="/gateway-firmware-manager" element={<GatewayFirmwareManager />} />
                    <Route path="/vlan-manager" element={<VlanManager />} />
                    <Route path="/bulk-user-upload" element={<BulkUserUpload />} />
                    <Route path="/network-scanner" element={<NetworkScanner />} />
                    <Route path="/sales-rollout" element={<SalesRolloutForecasting />} />
                    <Route path="/time-off/*" element={<TimeOffApp />} />
                    <Route path="/hr-portal/*" element={<HrPortalApp />} />
                </Route>
            </Routes>
        </>
    );
}

export default App;
